import React from "react";
import { withRouter } from "next/router";

import { requests } from "@deosite/agent";
import { ProductHeadOptionsContextProvider } from "@deosite/api/hooks/common/useProductHeadOptions";

import { createSearchUrl } from "@deosite/helpers/createSearchUrl";
import { CONNECTORS } from "@deosite/helpers/createSearchUrl";
import { readCacheFile, saveCacheFile } from "@deosite/helpers/cache";
import checkIfUrlIsForbidden from "@deosite/helpers/checkIfForbiddenUrlAndWriteToLogFile";

import CategoryPage from "./c/[...category]";
import ProductPage from "./productPage";

const CategoryAndProductPage = props => {
  return props.pageType === "product" ? (
    <ProductHeadOptionsContextProvider>
      <ProductPage {...props} />
    </ProductHeadOptionsContextProvider>
  ) : (
    <CategoryPage {...props} />
  );
};

CategoryAndProductPage.getInitialProps = async ctx => {
  if (await checkIfUrlIsForbidden(ctx)) {
    return {
      error: {
        statusCode: 404
      }
    };
  }

  const lastSlashPosition = ctx.asPath.lastIndexOf("/");
  const { language } = ctx;
  const questionMarkPosition = ctx.asPath.lastIndexOf("?");

  const url =
    questionMarkPosition > -1
      ? ctx.asPath.slice(1).slice(0, questionMarkPosition - 1)
      : ctx.asPath.slice(1);
  const cacheFilePath = `${language.language}/url/`;

  const {
    cacheFileExist,
    filenameCategoryCache,
    cachedRequest
  } = await readCacheFile({
    cacheFilePath,
    contextRequest: ctx.req,
    requestPath: ctx.asPath,
    separateFolder: true
  });

  let response = cachedRequest;
  let activeFiltersString;

  if (!cacheFileExist) {
    const [
      responseWithPotentialFiltersInUrl,
      responseWithoutPotentialFiltersInUrl
    ] = await Promise.all([
      requests.get(
        createSearchUrl("/deosite/url-map", [
          {
            field: "request_path",
            condition_type: "in",
            value: url,
            connector: CONNECTORS.AND
          }
        ]),
        false,
        null,
        language
      ),
      requests.get(
        createSearchUrl("/deosite/url-map", [
          {
            field: "request_path",
            condition_type: "in",
            value: ctx.asPath.slice(1).slice(0, lastSlashPosition - 1),
            connector: CONNECTORS.AND
          }
        ]),
        false,
        null,
        language
      )
    ]);

    response = responseWithoutPotentialFiltersInUrl;
    if (responseWithPotentialFiltersInUrl?.items?.length === 0) {
      activeFiltersString = ctx.asPath.slice(1).slice(lastSlashPosition);
    } else {
      response = responseWithPotentialFiltersInUrl;
      activeFiltersString =
        questionMarkPosition > -1
          ? ctx.asPath.slice(questionMarkPosition)
          : false;
    }
  }

  if (
    response?.items?.length === 0 ||
    (Object.keys(response).length === 0 && response.constructor === Object)
  ) {
    return {
      error: {
        statusCode: 404
      }
    };
  }

  if (cacheFileExist === false) {
    await saveCacheFile({
      cacheFilePath,
      filenameCategoryCache,
      result: { ...response, activeFiltersString },
      separateFolder: true
    });
  } else if (cachedRequest) {
    response = cachedRequest;
    activeFiltersString = cachedRequest?.activeFiltersString;
  }

  if (activeFiltersString) {
    activeFiltersString = activeFiltersString.replace(/(\?|\&)p=[0-9]*/g, "");
  }

  try {
    if (response.items[0].entity_type === "category") {
      return {
        ...(await CategoryPage.getInitialProps({
          ...ctx,
          query: {
            ...ctx.query,
            category: response.items[0].entity_id,
            requestPath: response.items[0].request_path,
            activeFiltersString
          }
        })),
        pageType: "category"
      };
    }

    return {
      ...(await ProductPage.getInitialProps({
        ...ctx,
        query: {
          ...ctx.query,
          productPage: response.items[0].metadata.sku
        }
      })),
      pageType: "product"
    };
  } catch (e) {
    console.log(
      `Category or product page error: ${e}, ctx query: ${JSON.stringify(
        ctx.query
      )} response item:  ${JSON.stringify(response?.items[0])}`
    );

    return {
      error: true
    };
  }
};

export default withRouter(CategoryAndProductPage);
